import { syncSpecialClaims } from '@msdk/definitions'
import { selector } from 'recoil'

import { $apiClient } from '../core'
import { $auth /* , $convertedToken */ } from './atoms'

/**
 *  selector that retrieves the access token.
 */
export const $refToken = selector<string>({
  key: 'ReferenceToken',
  get: ({ get }) => {
    const user = get($auth)
    return user?.access_token as string
  },
})

/**
 *  selector that retrieves the access token.
 */
export const $token = selector<string>({
  key: 'AccessToken',
  get: ({ get }) => {
    // Commenting following JWT login due to deprecation in favor of Ref token
    // const convertedToken = get($convertedToken)
    // return convertedToken as string

    // AccessToken has to be available and will contain the ref token
    const user = get($auth)
    return user?.access_token as string
  },
})

/**
 *  selector that retrieves the business unit.
 */
export const $businessUnit = selector<string>({
  key: 'BusinessUnit',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.business_unit as string
  },
})
/**
 *  selector that retrieves the  business unit language.
 */
export const $businessUnitLang = selector<string>({
  key: 'BusinessUnitLang',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.business_unit_lang as string
  },
})
/**
 *  selector that retrieves the  business unit timezone.
 */
export const $businessUnitTimezone = selector<string>({
  key: 'BusinessUnitTimezone',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.business_unit_timezone as string
  },
})
/**
 *  selector that retrieves the business unit external id.
 */
export const $businessUnitExternalId = selector<string>({
  key: 'BusinessUnitExternalId',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.business_unit_external_id as string
  },
})
/**
 *  selector that retrieves the user name.
 */
export const $name = selector<string>({
  key: 'UserName',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.name as string
  },
})
/**
 *  selector that retrieves the user firstname.
 */
export const $firstname = selector<string>({
  key: 'UserFirstname',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.firstname || ''
  },
})
/**
 *  selector that retrieves the user lastname.
 */
export const $lastname = selector<string>({
  key: 'UserLastname',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.lastname as string
  },
})
/**
 * selector that retrieves the user email.
 */
export const $email = selector<string>({
  key: 'UserEmail',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.email as string
  },
})

/**
 *  selector that retrieves the roles.
 */
export const $roles = selector<string[]>({
  key: 'Roles',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.role as string[]
  },
})
/**
 *  selector that retrieves the current tenant.
 */
export const $tenant = selector<string>({
  key: 'Tenant',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.tenant as string
  },
})
/**
 *  selector that retrieves the tenant external id.
 */
export const $tenantExternalId = selector<string>({
  key: 'TenantExternalId',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.tenant_extenal_id as string
  },
})
/**
 *  selector that retrieves the tenant guid.
 */
export const $tenantGuid = selector<string>({
  key: 'TenantGuid',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.tenant_guid as string
  },
})
/**
 *  selector that retrieves the user language.
 */
export const $lang = selector<string>({
  key: 'UserLanguage',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.lang as string
  },
})

export const $specialClaims = selector({
  key: 'SpecialClaims',
  get: async ({ get }) => {
    const client = get($apiClient)
    const token = get($refToken)
    /**
     * this selector could be accessed too early and the user could
     * not be authenticated yes this is why this check is in place
     */
    if (token) {
      try {
        const res = await client.read<string[]>('CMPS/api/User/SpecialClaims')
        const claims = res as string[]
        syncSpecialClaims(claims)
        return claims
      } catch (err) {
        console.warn('[SpecialClaims] error while getting special claims, proceding without them!', err)
        return []
      }
    }
    return []
  },
})

/**
 *  selector that retrieves the user name and last name.
 */
export const $fullname = selector<string>({
  key: 'UserFullName',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.fullname as string
  },
})
/**
 *  selector that retrieves the user sub.
 */
export const $sub = selector<string>({
  key: 'UserSub',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.sub as string
  },
})
/**
 *  selector that retrieves the user id.
 */
export const $userId = selector<string>({
  key: 'UserId',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.user_id as string
  },
})
/**
 *  selector that retrieves the user phone number.
 */
export const $userPhoneNumber = selector<string>({
  key: 'UserPhoneNumber',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.user_phonenumber as string
  },
})
/**
 *  selector that retrieves the user timezone.
 */
export const $userTimezone = selector<string>({
  key: 'UserTimezone',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.user_timezone as string
  },
})

/**
 *  selector that retrieves the user timezone.
 */
export const $userLastLogin = selector<string>({
  key: 'UserLastLogin',
  get: ({ get }) => {
    const user = get($auth)
    return user?.profile.lastlogin as string
  },
})
